import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Helmet from 'react-helmet';
import classNames from 'classnames';

import Button from '../../../../../../components/global/Button';
import { CARD_ELEMENT_TYPE, CARD_SIDE } from '../../../../constants';
import FormGroup from '../../../../../../components/global/FormGroup';
import withModal from '../../../../../../components/global/modal/withModal';
import useOpenAlert from '../../../../../../hooks/useOpenAlert';
import { getErrorString } from '../../../../../../util/normalize-errors';

const propTypes = {
  className: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  side: PropTypes.oneOf(Object.values(CARD_SIDE)).isRequired,
  elementsState: PropTypes.array.isRequired,
};

const AccountCardForm = ({
  className,
  onClose,
  side,
  elementsState,
  onElementConfigurationChange,
}) => {
  const openAlert = useOpenAlert();
  const [saving, setSaving] = useState(false);
  const [completedFields, setCompletedFields] = useState([]);

  const classes = classNames({
    'account-card-form': true,
    [className]: !!className,
  });

  const bodyClasses = [
    ...new Set((document?.body?.className ?? '').split(' ')),
  ].join(' ');

  const labeledTextElements = (elementsState ?? []).filter(
    element =>
      element.side === side &&
      element.cardElement.type === CARD_ELEMENT_TYPE.TEXT &&
      !!element.defaultCardElementConfiguration?.label,
  );

  const hasMissingTextElements =
    completedFields.length !== labeledTextElements.length;

  const onUpdateField = (element, text) => {
    const existentField = completedFields.find(
      field => field.element.cardElement.id === element.cardElement.id,
    );

    if (!existentField && text) {
      setCompletedFields([...completedFields, { element, text }]);
      return;
    }

    if (existentField && !text) {
      setCompletedFields(
        completedFields.filter(
          field => field.element.cardElement.id !== element.cardElement.id,
        ),
      );
      return;
    }

    if (existentField && text) {
      setCompletedFields([
        ...completedFields.filter(
          field => field.element.cardElement.id !== element.cardElement.id,
        ),
        { element, text },
      ]);
    }
  };

  const onSubmit = () => {
    if (saving) {
      return;
    }

    setSaving(true);
    try {
      completedFields.forEach(({ element, text }) => {
        onElementConfigurationChange(element, { text });
      });
      onClose();
    } catch (e) {
      openAlert({
        ty: 'error',
        message: getErrorString(e),
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={classes}>
      <Helmet>
        <body
          className={classNames({
            // Retain existing body classes
            [bodyClasses]: true,
            'body-card-select-modal-open': true,
            'body-card-select-modal-open-account-card': true,
          })}
        />
      </Helmet>

      <div className="card-element-control-header-dismiss">
        <Button
          btnStyle="link"
          onClick={onSubmit}
          disabled={hasMissingTextElements || saving}
        >
          {saving ? 'Saving...' : 'Done'}
        </Button>
      </div>
      <div className="card-element-control-header">
        <h3 className="card-element-control-header-title">
          Personalize your card
        </h3>
      </div>
      {labeledTextElements.map(element => {
        const completedField = completedFields.find(
          field => field.element.cardElement.id === element.cardElement.id,
        );

        return (
          <FormGroup
            id={element.id}
            name={`labeled-element-${element.id}`}
            value={completedField?.text ?? ''}
            label={element.defaultCardElementConfiguration?.label}
            placeholder={
              element.defaultCardElementConfiguration?.help_placeholder
            }
            onChange={({ target }) => onUpdateField(element, target.value)}
          />
        );
      })}
    </div>
  );
};

AccountCardForm.propTypes = propTypes;

export default withModal(AccountCardForm, {
  hideHeader: true,
  dismissible: false,
  size: 'sm',
  className: 'account-card-form-modal modal-floating-panel',
  overlayClassName: 'account-card-form-modal-overlay',
  shouldCloseOnOverlayClick: false,
});
